import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { convertTimestampsToISO } from '../../utils/firebaseUtils.js';
import {
  createPrescription as createPrescriptionService,
  getPatientPrescriptions as getPatientPrescriptionsService,
  updatePatientPharmacy as updatePatientPharmacyService
} from '../../services/prescriptionService';
import { getPatient as getPatientByIdService } from '../../services/patientService';

// Async thunks
export const createPrescription = createAsyncThunk(
  'prescriptions/createPrescription',
  async ({ patientId, medications, pharmacy }) => {
    // Get patient's facility ID using service layer
    const patient = await getPatientByIdService(patientId);
    const facilityId = patient.facilityId;

    const prescriptionData = {
      patientId,
      medications,
      pharmacy,
      facilityId,
      status: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const prescription = await createPrescriptionService(prescriptionData);
    return prescription;
  }
);

export const fetchPatientPrescriptions = createAsyncThunk(
  'prescriptions/fetchPatientPrescriptions',
  async (patientId) => {
    const prescriptions = await getPatientPrescriptionsService(patientId);
    return prescriptions.map(prescription => convertTimestampsToISO(prescription));
  }
);

export const updatePharmacy = createAsyncThunk(
  'prescriptions/updatePharmacy',
  async ({ patientId, pharmacyDetails }) => {
    const pharmacy = await updatePatientPharmacyService(patientId, pharmacyDetails);
    return pharmacy;
  }
);

const prescriptionsSlice = createSlice({
  name: 'prescriptions',
  initialState: {
    prescriptions: [],
    loading: false,
    error: null
  },
  reducers: {
    clearPrescriptions: (state) => {
      state.prescriptions = [];
    },
    setPrescriptions: (state, action) => {
      state.prescriptions = action.payload;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPrescription.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPrescription.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptions.push(action.payload);
      })
      .addCase(createPrescription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPatientPrescriptions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPatientPrescriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptions = action.payload;
      })
      .addCase(fetchPatientPrescriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

export const { clearPrescriptions, setPrescriptions } = prescriptionsSlice.actions;
export default prescriptionsSlice.reducer;
